import React, { SyntheticEvent } from 'react';
import { Checkbox, CheckboxProps, Grid, Input } from 'semantic-ui-react';
import { Field, FormikProps } from 'formik';
import styled from 'styled-components';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import SectionLabel from '../../../../../../components/SectionLabel';
import { FieldLabel } from '../../../../../../styles';
import CustomDatePicker from '../../../../../../components/CustomDatePicker';
import AppMaskedInput from '../../../../../../components/MaskedInput';
import { DATE_INPUT_MASK } from '../../../../../../services/masks';
import { claimCoverageOptions } from '../../../../constants/claimCoverages';
import { ClaimCoverageKey } from '../../../../types/ClaimCoverageTypes';
import { OtherProductClaimInitialValuesType } from '../../Formik/types';

const CoverageOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 48px;
  row-gap: 14px;
  margin: 8px 0 16px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    row-gap: 12px;
  }
`;

const CoverageDescription = styled.div`
  font-family: 'Open Sans', sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0;
  color: #364445;
`;

const CoverageHint = styled.span`
  font-weight: 400;
  color: #aaadb1;
`;

const RequiredMark = styled.span`
  color: #db2828;
  margin-left: 2px;
`;

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean']
  ]
};

/** Evita deslocamento de um dia ao parsear YYYY-MM-DD (UTC). */
const parseLocalDate = (value?: string | Date | null): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const ClaimDetailsStep = ({
  values,
  setFieldValue
}: FormikProps<OtherProductClaimInitialValuesType>) => {
  const selectedCoverages = values.claimEstate.coverages || [];

  const handleCoverageChange = (coverage: ClaimCoverageKey, checked: boolean) => {
    const nextCoverages = checked
      ? [...selectedCoverages, coverage]
      : selectedCoverages.filter(selected => selected !== coverage);

    setFieldValue('claimEstate.coverages', nextCoverages);
  };

  return (
    <div>
      <SectionLabel text="Detalhes do evento" />
      <Grid>
        <Grid.Row>
          <Grid.Column width={4}>
            <FieldLabel>Data</FieldLabel>
            <Field
              name="claimEstate.eventDate"
              selected={parseLocalDate(values.claimEstate.eventDate as any)}
              component={CustomDatePicker}
              customInput={<AppMaskedInput mask={DATE_INPUT_MASK} />}
              dateFormat="dd/MM/yyyy"
              onChange={(date: Date, _e: SyntheticEvent) => {
                setFieldValue('claimEstate.eventDate', date || null);
              }}
            />
          </Grid.Column>
          <Grid.Column width={4}>
            <FieldLabel>Horário</FieldLabel>
            <Field
              value={values.claimEstate.eventTime || ''}
              component={Input}
              fluid
              type="time"
              onChange={(_e: any, { value }: { value: string }) =>
                setFieldValue('claimEstate.eventTime', value)
              }
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={16}>
            <FieldLabel required>Descrição do Evento</FieldLabel>
            <ReactQuill
              theme="snow"
              value={values.claimEstate.eventDescription || ''}
              modules={quillModules}
              onChange={(content: string) =>
                setFieldValue('claimEstate.eventDescription', content)
              }
              style={{ marginBottom: 0 }}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <SectionLabel text="Coberturas" />
      <CoverageDescription>
        Selecione as coberturas acionadas <CoverageHint>(mínimo 1)</CoverageHint>
        <RequiredMark>*</RequiredMark>
      </CoverageDescription>
      <CoverageOptions>
        {claimCoverageOptions.map(coverage => (
          <Checkbox
            key={coverage.value}
            id={`claim-coverage-${coverage.value}`}
            label={coverage.label}
            checked={selectedCoverages.includes(coverage.value)}
            onChange={(_event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) =>
              handleCoverageChange(coverage.value, Boolean(data.checked))
            }
          />
        ))}
      </CoverageOptions>
    </div>
  );
};

export default ClaimDetailsStep;
